<div class="page">
  <div class="page-head">
    <div>
      <h2>{{ pageTitle() || 'All Members' }}</h2>
      <p>Every insured member across all partners and clients — search, filter and drill in.</p>
    </div>
    <p-button label="Reset Filters" icon="pi pi-filter-slash" [outlined]="true" (onClick)="resetFilters()" />
  </div>

  <!-- Members who paid but never got a policy number. Renders nothing when there are none. -->
  <app-unissued-panel (issued)="load()" />

  <div class="card">
    <div class="toolbar">
      <p-iconfield iconPosition="left" class="search-field">
        <p-inputicon styleClass="pi pi-search" />
        <input type="text" pInputText placeholder="Search name, uid, mobile, email, policy or loan..."
          [ngModel]="search()" (ngModelChange)="onSearch($event)" />
      </p-iconfield>

      <div class="filter">
        <label>Partner</label>
        <p-select [ngModel]="fPartner()" (ngModelChange)="fPartner.set($event); onFilter()"
          [options]="partnerOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label"
          [showClear]="true" placeholder="All" appendTo="body" />
      </div>
      <div class="filter">
        <label>Client</label>
        <p-select [ngModel]="fClient()" (ngModelChange)="fClient.set($event); onFilter()"
          [options]="clientOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label"
          [showClear]="true" placeholder="All" appendTo="body" />
      </div>
      <div class="filter">
        <label>Source</label>
        <p-select [ngModel]="fSource()" (ngModelChange)="fSource.set($event); onFilter()"
          [options]="sourceOptions()" optionLabel="label" optionValue="value"
          [showClear]="true" placeholder="All" appendTo="body" />
      </div>
      <div class="filter">
        <label>Payment</label>
        <p-select [ngModel]="fPay()" (ngModelChange)="fPay.set($event); onFilter()"
          [options]="payOptions()" optionLabel="label" optionValue="value"
          [showClear]="true" placeholder="All" appendTo="body" />
      </div>
      <div class="filter">
        <label>KYC</label>
        <p-select [ngModel]="fKyc()" (ngModelChange)="fKyc.set($event); onFilter()"
          [options]="kycOptions()" optionLabel="label" optionValue="value"
          [showClear]="true" placeholder="All" appendTo="body" />
      </div>

      <span class="count">{{ total() }} members</span>
    </div>

    <p-table [value]="rows()" [loading]="loading()" dataKey="id" [lazy]="true" [lazyLoadOnInit]="false"
      [paginator]="true" [rows]="pageSize()" [totalRecords]="total()" [rowsPerPageOptions]="[10, 25, 50]"
      (onLazyLoad)="onLazy($event)" styleClass="p-datatable-sm"
      [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}">
      <ng-template pTemplate="header">
        <tr>
          <th>Member</th>
          <th>Contact</th>
          <th>Policy</th>
          <th>Source</th>
          <th class="ta-r">Premium</th>
          <th>Payment</th>
          <th>KYC</th>
          <th>Created</th>
          <th class="ta-r" style="width: 130px">Actions</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-r>
        <tr class="row-click" (click)="openLookup(r)" pTooltip="Open member details" tooltipPosition="top" [showDelay]="500">
          <td class="name">
            {{ r.fullName || ('Member #' + r.id) }}
            <small class="sub mono">{{ r.memberUniqueId || '—' }}{{ r.memberSeriesNo ? ' · ' + r.memberSeriesNo : '' }}</small>
          </td>
          <td>
            <span class="mono">{{ r.mobileNumber || '—' }}</span>
            @if (r.email) { <small class="sub">{{ r.email }}</small> }
          </td>
          <td>
            {{ r.masterPolicyName || '—' }}
            @if (r.policyNo) {
              <small class="sub mono">{{ r.policyNo }}</small>
            } @else if (isStranded(r)) {
              <!-- Paid, but no number was ever minted — say so here rather than showing a blank. -->
              <small class="sub warn">not issued</small>
            }
          </td>
          <td>
            <span class="src" [class.client]="sourceOf(r).kind === 'Client'">{{ sourceOf(r).kind }}</span>
            <small class="sub">{{ sourceOf(r).name }}</small>
          </td>
          <td class="ta-r amount">
            {{ inr(r.grossAmt) }}
            @if (r.paidAmount != null && r.paymentStatus === 'PAID') {
              <small class="sub ok">paid {{ inr(r.paidAmount) }}</small>
            }
          </td>
          <td><p-tag [value]="r.paymentStatus || 'UNPAID'" [severity]="paySeverity(r.paymentStatus)" /></td>
          <td><p-tag [value]="r.kycStatus || '—'" [severity]="kycSeverity(r.kycStatus)" /></td>
          <td class="nowrap">{{ r.createdAt ? (r.createdAt | indDate) : '—' }}</td>
          <td class="ta-r actions" (click)="$event.stopPropagation()">
            <p-button icon="pi pi-user" [rounded]="true" [text]="true" severity="secondary"
              pTooltip="Open in Member Lookup (details, nominees, payments)" (onClick)="openLookup(r)" />
            <p-button icon="pi pi-indian-rupee" [rounded]="true" [text]="true" severity="secondary"
              pTooltip="View payments" (onClick)="openPayments(r)" />
            @if (isStranded(r)) {
              <p-button [icon]="issuingId() === r.id ? 'pi pi-spin pi-spinner' : 'pi pi-verified'"
                [rounded]="true" [text]="true" severity="warn"
                [disabled]="!canIssue(r) || issuingId() !== null"
                [pTooltip]="canIssue(r)
                  ? 'Paid but never issued — mint the policy number and send the certificate'
                  : 'Paid but never issued. Verify the member by OTP first; issuance then runs automatically.'"
                (onClick)="issuePolicy(r)" />
            }
            @if (hasDocs(r)) {
              <p-button [icon]="docBusy(r) ? 'pi pi-spin pi-spinner' : 'pi pi-file-export'"
                [rounded]="true" [text]="true" severity="secondary"
                [disabled]="generatingId() !== null || letteringId() !== null"
                pTooltip="Documents (certificate, welcome letter)" (onClick)="openDocMenu(r, $event, docMenu)" />
              <p-menu #docMenu [popup]="true" [model]="docItems()" appendTo="body" />
            }
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="9">
            <div class="state"><i class="pi pi-users"></i><p>No members match these filters.</p></div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>
</div>
